import { Link as GatsbyLink } from "gatsby";
import { getPathWithCountryCode } from "../../utils/path";
import {
  ExternalLinkBlock,
  HubSpotLinkBlock,
  InternalLinkBlock,
  RichTextAssetLinkBlock,
  RichTextExternalLinkBlock,
  RichTextHubSpotLinkBlock,
  RichTextInternalLinkBlock
} from "./types";
import type { AssetLinkBlock, LinkData } from "./types";
import type { AnchorLinkActionProps as OriginalAnchorLinkActionProps } from "@bmi-digital/components/anchor-link";
import type { ButtonActionProps } from "@bmi-digital/components/button";
import type { BasePageInfoData } from "../PageInfo";
import type { PromoData } from "../Promo";
import type { GTM } from "@bmi-digital/components";

type AnchorLinkActionProps = Omit<
  OriginalAnchorLinkActionProps,
  "gtm" | "to"
> & {
  to?: string;
  gtm: GTM;
};

export const getCTA = (
  data: Pick<PromoData, "cta"> | Pick<BasePageInfoData, "path">,
  countryCode: string,
  linkLabel?: string
): AnchorLinkActionProps | null => {
  if ("cta" in data) {
    if (
      !data.cta ||
      data.cta.length === 0 ||
      data.cta[0].blockType === "visualiser-link-block" ||
      data.cta[0].blockType === "calculator-link-block" ||
      data.cta[0].blockType === "dialog-link-block"
    ) {
      return null;
    }

    return toAnchorLinkActionProps(data.cta[0], countryCode);
  }

  const to = getPathWithCountryCode(countryCode, data.path).replace(
    /\/+/gi,
    "/"
  );
  return {
    to: to,
    component: GatsbyLink,
    gtm: { id: "cta-click1", action: to, label: linkLabel }
  };
};

const internalHosts = process.env.GATSBY_INTERNAL_HOSTS?.split(",") || [
  "www.bmigroup.com"
];

export const isExternalUrl = (url: string): boolean => {
  try {
    const linkUrl = new URL(url);
    return !internalHosts.includes(linkUrl.host);
  } catch (e) {
    return false;
  }
};

const checkUrlAction = (url: string): boolean => {
  const actionUrls = ["mailto:", "tel:", "callto:"];
  return actionUrls.some((actionUrl) => url.startsWith(actionUrl));
};

export const toAnchorLinkActionProps = (
  link:
    | HubSpotLinkBlock
    | InternalLinkBlock
    | ExternalLinkBlock
    | AssetLinkBlock
    | RichTextInternalLinkBlock
    | RichTextExternalLinkBlock
    | RichTextAssetLinkBlock
    | RichTextHubSpotLinkBlock,
  countryCode?: string
): AnchorLinkActionProps => {
  if (
    link.blockType === "hubspot-link-block" ||
    link.blockType === "rich-text-hubspot-cta-link-block"
  ) {
    const to = `${process.env.GATSBY_HUBSPOT_CTA_URL}${process.env.GATSBY_HUBSPOT_ID}/${link.hubSpotCTAID}`;
    return {
      href: to,
      external: true,
      gtm: {
        id: "cta-click1",
        action: to,
        label: link.label
      }
    };
  }

  if (
    link.blockType === "asset-link-block" ||
    link.blockType === "rich-text-asset-link-block"
  ) {
    return {
      href: link.asset.url,
      download: true,
      external: true,
      gtm: {
        id: "cta-click1",
        action: link.asset.url,
        label: link.label
      }
    };
  }

  if (
    link.blockType === "internal-link-block" ||
    link.blockType === "rich-text-internal-link-block"
  ) {
    const to = getPathWithCountryCode(
      countryCode,
      link.linkedPage.value.path
    ).replace(/\/+/gi, "/");

    return {
      component: GatsbyLink,
      to,
      gtm: { id: "cta-click1", action: to, label: link.label }
    };
  }

  return {
    href: link.url,
    external:
      (!checkUrlAction(link.url) && isExternalUrl(link.url)) || undefined,
    gtm: { id: "cta-click1", action: link.url, label: link.label }
  };
};

export const toButtonActionProps = (
  link: LinkData,
  countryCode?: string,
  onClick?: ButtonActionProps["onClick"]
): ButtonActionProps => {
  if (
    link.blockType === "visualiser-link-block" ||
    link.blockType === "rich-text-visualiser-link-block"
  ) {
    return {
      onClick,
      gtm: {
        id: "cta-visualiser1",
        action: "visualiser",
        label: link.label
      }
    };
  }

  if (
    link.blockType === "calculator-link-block" ||
    link.blockType === "rich-text-calculator-link-block"
  ) {
    return {
      onClick,
      gtm: {
        id: "cta-calculator1",
        action: "calculator",
        label: link.label
      }
    };
  }

  if (
    link.blockType === "dialog-link-block" ||
    link.blockType === "rich-text-dialog-link-block"
  ) {
    return {
      onClick,
      gtm: { id: "cta-click1", action: "dialog", label: link.label }
    };
  }

  return toAnchorLinkActionProps(link, countryCode);
};
